import style from "./CertificateDetails.module.css";
import { useGetCertificateByID } from "@/features/certificates/hooks/useGetCertificateByID";

import Container from "@/layouts/container/Container";
import FullPageLoader from "@/ui/fullPageLoader/FullPageLoader";

import Icon from "@/ui/icon/Icon";
import { useParams, Navigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

import { useUser } from "@/features/auth/hooks/useUser";

import { successToast, errorToast } from "@/ui/toast/Toast";

function CertificateDetails() {
	const { t } = useTranslation();
	const { id } = useParams();
	const { certificate, isPending, isError } = useGetCertificateByID(id);
	const { user, isLoggedIn, isPending: userIsPending } = useUser();
	const { course } = certificate;

	// Function to copy link to clipboard
	const handleCopyLink = async () => {
		try {
			const currentUrl = window.location.href;
			await navigator.clipboard.writeText(currentUrl);
			// i18n.t, not t: the toast follows the language current when it shows.
			successToast(i18n.t("courses:certificate.details.copied"));
		} catch (err) {
			errorToast(i18n.t("courses:certificate.details.copyFailed"));
		}
	};

	const handleLinkedInShare = () => {
		if (!window || !course || !certificate) {
			console.error("Missing required data to share certificate.");
			return;
		}

		const currentUrl = encodeURIComponent(window.location.href);
		// The certificate image is drawn in the holder's certificate language,
		// which this page is not told, so the share text keeps the Bosnian name
		// rather than guess from the viewer's site language.
		const certificateName = encodeURIComponent(
			course?.name || "Dekamas Certificate"
		);
		const organizationID = encodeURIComponent("106153091");

		let issueMonth = "";
		let issueYear = "";

		if (certificate?.time_issued) {
			const issueDate = new Date(certificate.time_issued);
			issueMonth = issueDate.getMonth() + 1;
			issueYear = issueDate.getFullYear();
		}

		const credentialId = encodeURIComponent(
			certificate?.certificate_id || ""
		);

		const linkedInUrl = `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${certificateName}&organizationId=${organizationID}&issueMonth=${issueMonth}&issueYear=${issueYear}&certId=${credentialId}&certUrl=${currentUrl}&certificationTimeUnit=LIFETIME`;

		const newWindow = window.open(
			linkedInUrl,
			"_blank",
			"noopener,noreferrer,width=800,height=600"
		);
	};

	if (isPending || userIsPending) return <FullPageLoader />;
	if (isError || !certificate?.is_unlocked) return <Navigate to="/404" />;

	return (
		<>
			<div className={style.background}>
				<Container size="mini">
					<div className={style.holder}>
						<div className={style.header}>
							<div className={style.subheader}>
								<Link
									to={
										isLoggedIn
											? `/u/${user?.username}`
											: "/"
									}
									className={style.goBack}
								>
									<div className={style.icoHolder}>
										<Icon
											icon="fa-arrow-left"
											iconType="fa-solid"
										/>
									</div>
								</Link>
								{isLoggedIn && (
									<div className={style.language}>
										<Link to="/settings">
											<div className={style.icoHolder}>
												<Icon icon="fa-earth" />
											</div>
										</Link>
									</div>
								)}
							</div>

							<h2>{t("courses:certificate.details.title")}</h2>
							<span>#{certificate?.certificate_id}</span>
						</div>
						<div className={style.body}>
							<img src={certificate?.image_png}></img>
						</div>
						<div className={style.footer}>
							<div className={style.buttons}>
								<a
									href={certificate?.image_pdf}
									download={true}
									target="_blank"
									rel="noreferrer"
									className={style.btn}
								>
									<Icon icon="fa-download" />
									<span>{t("courses:certificate.details.downloadPdf")}</span>
								</a>
								<div
									className={style.btn}
									onClick={handleCopyLink}
								>
									<Icon icon="fa-chain" />
									<span>{t("courses:certificate.details.copyLink")}</span>
								</div>
							</div>

							<div
								className={style.btn}
								onClick={handleLinkedInShare}
							>
								<Icon icon="fa-share" />
								<span>{t("courses:certificate.details.shareLinkedIn")}</span>
							</div>
						</div>
					</div>
				</Container>
			</div>
		</>
	);
}

export default CertificateDetails;
